import React from "react";
import { ToolIcon, brandColorFor } from "../classic/panels";
import { toolGlyphIdFor } from "../classic/toolIcons";
import { ChevronRight, ArrowLeft, ArrowRight, Sparkles, Layers, Clock, AppWindow, ListTree } from "lucide-react";
import { apiInstance as api } from "../../../../hooks/useApi";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Shim, fmtMins } from "../classic/micro";
import { fmtDay as fmtDate } from "./drilldownRoutes";
import { BeatList, splitBeats } from "../../screens/graphical/drill/StoryChrome";

/**
 * DayPicture — the three-layer AI read of "what this person worked on".
 *
 *   Layer 1  the picture   3-4 AI topics over the window's real work
 *   Layer 2  a topic       the real items (windows/pages) under that topic
 *   Layer 3  an item       one window/page, in full detail
 *
 * The topics come from `/brain-person/:id/day-picture` — the model only groups
 * and names REAL items (window/page titles + minutes) from both tracking
 * sources; it never invents work. The layers are progressive in-page state, so
 * a topic is always specific to what was clicked, and an item is specific to its
 * topic — nothing out of reach.
 */
export function DayPicture({ userId, range, name, palette, onSummary }) {
  const t = useDashTheme();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [topicId, setTopicId] = React.useState(null); // L2 selection
  const [itemIdx, setItemIdx] = React.useState(null);  // L3 selection
  const [hotThread, setHotThread] = React.useState(null); // rows-mode cross-light

  const start = range?.start;
  const end = range?.end;

  // Layer 3 on-demand: when an item is opened, pull its specific detail from
  // Qdrant (screen content -> AI account) + Mongo (occurrences).
  const [detail, setDetail] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);

  // Layer 2 on-demand: when a topic card is opened, pull that thread's detailed
  // + graphical read — when it happened (per-hour) + where (apps) from Mongo,
  // and an AI summary + points from Qdrant.
  const [topicDetail, setTopicDetail] = React.useState(null);
  const [topicDetailLoading, setTopicDetailLoading] = React.useState(false);

  React.useEffect(() => {
    if (!userId || !start || !end) return undefined;
    let live = true;
    setLoading(true); setErr(null); setTopicId(null); setItemIdx(null);
    const qs = new URLSearchParams({ start: fmtDate(start), end: fmtDate(end) }).toString();
    api.get(`/api/v1/brain-person/${encodeURIComponent(userId)}/day-picture?${qs}`)
      .then((res) => { if (live) setData(res.data?.data || null); })
      .catch((e) => { if (live) setErr(e?.response?.data?.error?.message || e.message || "Could not read the day."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [userId, start, end]);

  const topicForFetch = (data?.topics || []).find((x) => x.id === topicId) || null;
  const itemForFetch = topicForFetch && itemIdx != null ? topicForFetch.items[itemIdx] : null;
  const itemTitleForFetch = itemForFetch ? itemForFetch.title : null;
  React.useEffect(() => {
    if (!itemTitleForFetch || !userId || !start || !end) { setDetail(null); return undefined; }
    let live = true;
    setDetailLoading(true); setDetail(null);
    const qs = new URLSearchParams({ start: fmtDate(start), end: fmtDate(end), q: itemTitleForFetch }).toString();
    api.get(`/api/v1/brain-person/${encodeURIComponent(userId)}/item-detail?${qs}`)
      .then((res) => { if (live) setDetail(res.data?.data || null); })
      .catch(() => { if (live) setDetail(null); })
      .finally(() => { if (live) setDetailLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemTitleForFetch, userId, start, end]);

  // L2 on-demand: fetch the opened topic's detailed + graphical read. Fires only
  // when a topic is selected (not on L1, and not re-fetched when drilling L2->L3
  // since topicId is unchanged). Sends the topic's real item titles so the
  // server can scope the timeline/apps to exactly this thread.
  React.useEffect(() => {
    if (topicId == null || !userId || !start || !end) { setTopicDetail(null); return undefined; }
    const tp = (data?.topics || []).find((x) => x.id === topicId);
    if (!tp) { setTopicDetail(null); return undefined; }
    let live = true;
    setTopicDetailLoading(true); setTopicDetail(null);
    api.post(`/api/v1/brain-person/${encodeURIComponent(userId)}/topic-detail`, {
      start: fmtDate(start), end: fmtDate(end),
      title: tp.title,
      items: (tp.items || []).map((it) => it.title),
    })
      .then((res) => { if (live) setTopicDetail(res.data?.data || null); })
      .catch(() => { if (live) setTopicDetail(null); })
      .finally(() => { if (live) setTopicDetailLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, userId, start, end, data]);


  const who = (name || "This person").split(" ")[0];
  const topics = data?.topics || [];
  const graphical = Array.isArray(palette) && palette.length > 0;

  /* Tell the host panel what the picture holds (thread count · active
     minutes · whether the model grouped it) so its head can carry the
     chips instead of a grey subtitle. */
  const onSummaryRef = React.useRef(onSummary);
  onSummaryRef.current = onSummary;
  React.useEffect(() => {
    if (typeof onSummaryRef.current !== "function") return;
    if (loading) { onSummaryRef.current(null); return; }
    const tps = data?.topics || [];
    onSummaryRef.current({
      threads: tps.length,
      minutes: data?.totalMinutes || tps.reduce((a, x) => a + (x.minutes || 0), 0),
      ai: Boolean(data?.headline),
    });
  }, [loading, data]);
  const topic = topics.find((x) => x.id === topicId) || null;
  const item = topic && itemIdx != null ? topic.items[itemIdx] : null;

  if (loading) {
    // Skeleton that mirrors the real L1: the hero (headline + split + rhythm)
    // then the topic-card grid — so nothing jumps when it arrives.
    return (
      <div>
        <div style={panel(t, { marginBottom: 18, background: `linear-gradient(180deg, ${alpha(t.c.primary, 0.07)}, ${t.c.surface} 58%)` })}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 15 }}>
            <Shim w={150} h={12} r={6} />
            <span style={{ marginLeft: "auto" }}><Shim w={116} h={11} r={6} /></span>
          </div>
          <Shim h={20} w="90%" />
          <Shim h={20} w="64%" style={{ marginTop: 9 }} />
          <Shim h={11} r={999} style={{ marginTop: 18 }} />
          <div style={{ display: "flex", gap: 18, marginTop: 13 }}>
            {[90, 120, 80, 100].map((w, k) => <Shim key={k} w={w} h={11} r={6} />)}
          </div>
          <Shim h={34} r={9} style={{ marginTop: 20 }} />
        </div>
        <Shim w={190} h={11} r={5} style={{ margin: "0 2px 12px" }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {[0, 1, 2, 3].map((k) => <SkTopicCard key={k} t={t} />)}
        </div>
      </div>
    );
  }
  if (err) {
    return <Note t={t}>The picture could not be read. {err}</Note>;
  }
  if (!topics.length) {
    return <Note t={t}>No tracked work to picture in this window for {who}.</Note>;
  }

  const total = data.totalMinutes || topics.reduce((a, x) => a + x.minutes, 0);
  // Topic colours come from the theme's VALIDATED categorical spread (t.series,
  // light/dark aware) — clean, distinct hues. The old accentFor(seed) hashed
  // "topic-N" into muddy maroon/olive that clashed with the theme.
  /* Callers may pin their own categorical palette — the graphical drill
     passes its inks (blue/violet/amber/pink; never green) so threads match
     the rest of that dashboard. Classic keeps the token series. */
  const seriesColors = Array.isArray(palette) && palette.length
    ? palette
    : (Array.isArray(t.series) && t.series.length ? t.series : [t.c.primary]);
  const topicColor = (i) => seriesColors[i % seriesColors.length];
  /* Inside ONE thread the pieces get their own categorical inks — tints of a
     single blue read as one dull wash (user, 2026-08-30). Teal is added so
     five pieces never repeat. */
  const teal = t.isDark ? "#3FC9B0" : "#0E9F8A";
  const itemColors = [...seriesColors, teal];
  const itemColor = (k) => itemColors[k % itemColors.length];

  // ── LAYER 3 · one item, in full detail ────────────────────────────────
  if (item) {
    const ci = topics.indexOf(topic);
    /* Graphical mode: the detail pages are ALWAYS the theme primary —
       the thread's categorical colour stays in the L1 list and share bar,
       it does not paint the whole page it opens. */
    const color = Array.isArray(palette) && palette.length ? t.c.primary : topicColor(ci);
    const shareTopic = topic.minutes ? Math.round((item.minutes / topic.minutes) * 100) : 0;
    const shareDay = total ? Math.round((item.minutes / total) * 100) : 0;
    return (
      <div>
        <Crumbs t={t} trail={[
          { label: "The picture", on: () => { setTopicId(null); setItemIdx(null); } },
          { label: topic.title, on: () => setItemIdx(null) },
          { label: "Detail" },
        ]} onBack={() => setItemIdx(null)} />
        <div style={panel(t)}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 11 }}>
            {item.app ? ((toolGlyphIdFor(item.app) || brandColorFor(item.app, t)) ? <span title={item.app} style={{ display: "inline-flex", verticalAlign: "middle" }}><ToolIcon name={item.app} size={20} /></span> : <Badge t={t} color={color}>{item.app}</Badge>) : null}
            <span style={{ fontSize: 11, color: t.c.textTertiary, fontWeight: 500 }}>within {topic.title}</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.c.text, lineHeight: 1.4, marginBottom: 16, letterSpacing: "-0.2px" }}>{item.title}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(116px, 1fr))", gap: 10, marginBottom: 18 }}>
            <Stat t={t} k="Active time" v={fmtMins(item.minutes)} />
            <Stat t={t} k="Of this thread" v={`${shareTopic}%`} bar={shareTopic} c={color} />
            <Stat t={t} k="Of the day" v={`${shareDay}%`} bar={shareDay} c={t.c.primary} />
          </div>

          {/* ON-DEMAND · pulled from screen content -> AI + activity records when opened */}
          <Overline t={t} accent={color} icon={<Sparkles size={12} />} label="What went on here" />
          {detailLoading ? (
            <>
              <Shim h={13} /><Shim h={13} w="88%" style={{ marginTop: 8 }} /><Shim h={13} w="72%" style={{ marginTop: 8 }} />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 16 }}>
                {[64, 64, 64, 64, 64, 64].map((w, k) => <Shim key={k} w={w} h={24} r={8} />)}
              </div>
            </>
          ) : detail?.detail ? (
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.75, color: t.c.text }}>{detail.detail}</p>
          ) : (
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: t.c.textTertiary }}>
              A written account of this window is still being prepared — the times below are exact,
              and the read appears once its content is indexed.
            </p>
          )}

          {detail?.occurrences?.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <Overline t={t} icon={<Clock size={12} />} label="When it was active"
                right={detail.totalMinutes ? <span style={{ fontSize: 10.5, fontWeight: 700, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{fmtMins(detail.totalMinutes)} total</span> : null} />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {detail.occurrences.map((o, i) => (
                  <span key={i} title={`${o.at} · ${fmtMins(o.minutes)}${o.app ? ` · ${o.app}` : ""}`}
                    style={{ fontSize: 11, fontWeight: 500, color: t.c.textSecondary, background: t.c.surfaceMuted, boxShadow: `inset 0 0 0 1px ${t.c.border}`, borderRadius: 8, padding: "5px 9px", fontVariantNumeric: "tabular-nums" }}>
                    <b style={{ color: t.c.text, fontWeight: 700 }}>{o.at}</b> · {fmtMins(o.minutes)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 16, paddingTop: 13, borderTop: `1px solid ${t.c.border}`, fontSize: 10.5, lineHeight: 1.55, color: t.c.textTertiary }}>
            Pulled on demand for this window from activity analysis. Personal details are removed.
          </div>
        </div>
      </div>
    );
  }

  // ── LAYER 2 · one topic — ON DEMAND, detailed + graphical ─────────────
  // Opening a card fetches this thread's own read: a written account (hero), a
  // donut of what it was made of, a per-hour chart of WHEN it happened, WHERE it
  // ran (apps), and the items as doors into L3 — all scoped to this topic.
  if (topic) {
    const i = topics.indexOf(topic);
    const color = Array.isArray(palette) && palette.length ? t.c.primary : topicColor(i);
    const topItem = Math.max(...topic.items.map((x) => x.minutes), 1);
    const td = topicDetail;

    // The read is ALWAYS present so every L2 makes plain sense: the model's
    // written account when this thread's content is indexed, otherwise a summary
    // composed from the real figures already on the page. Never a blank apology.
    const topApp = td && Array.isArray(td.apps) && td.apps.length ? td.apps[0] : null;
    const whenClause = td && td.span
      ? ` from ${fmtHour(td.span.from)} to ${fmtHour(td.span.to)}${td.peakHour != null ? `, densest around ${fmtHour(td.peakHour)}` : ""}`
      : "";
    const read = (td && td.detail)
      ? { kind: "written read", ai: true, summary: td.detail, points: Array.isArray(td.points) ? td.points : [] }
      : {
        kind: "computed read", ai: false,
        summary: `This thread took about ${fmtMins(topic.minutes)} across ${topic.items.length} window${topic.items.length === 1 ? "" : "s"}${topApp ? `, mostly in ${topApp.app}` : ""}${whenClause}.`,
        points: topic.items.slice(0, 3).map((it) => {
          const share = topic.minutes ? Math.round((it.minutes / topic.minutes) * 100) : 0;
          return `${it.title} — ${fmtMins(it.minutes)}${share ? ` · ${share}%` : ""}`;
        }),
      };

    return (
      <div>
        <Crumbs t={t} trail={[
          { label: "The picture", on: () => { setTopicId(null); setItemIdx(null); } },
          { label: topic.title },
        ]} onBack={() => setTopicId(null)} />

        {/* header */}
        <div style={panel(t, { marginBottom: 14 })}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
            <NumTile color={color} n={i + 1} size={42} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ ...t.font.statLabel, color, marginBottom: 3 }}>Thread {i + 1} of {topics.length} · {topic.items.length} item{topic.items.length === 1 ? "" : "s"} · {total ? Math.round((topic.minutes / total) * 100) : 0}% of the day</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.c.text, letterSpacing: "-0.4px", lineHeight: 1.25 }}>{topic.title}</div>
              {topic.overview ? <p style={{ margin: "7px 0 0", fontSize: 12.5, lineHeight: 1.6, color: t.c.textSecondary }}>{topic.overview}</p> : null}
            </div>
            <Metric t={t} value={fmtMins(topic.minutes)} caption="tracked" />
          </div>
        </div>

        {/* THE READ — the same card anatomy as the hour write-up (2026-08-30):
            rail · ink tile · kicker · lead line · chips, then the beats and
            the numbered points, and an honest footer. */}
        {(() => {
          const sentences = splitBeats(read.summary);
          const lead = (sentences[0] || read.summary || "").trim();
          const rest = sentences.slice(1).join(" ").trim();
          const items = topic.items.length;
          return (
            <div style={{
              position: "relative", overflow: "hidden", marginBottom: 14,
              borderRadius: t.r.card, padding: "18px 20px 16px",
              background: `linear-gradient(180deg, ${alpha(color, 0.09)}, ${t.c.surface} 56%)`,
              boxShadow: `0 0 0 1px ${alpha(color, 0.3)}, 0 1px 2px ${t.c.shadowColor}, 0 18px 40px -26px ${alpha(color, 0.5)}`,
            }}>
              <span aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color }} />
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <span aria-hidden="true" style={{
                  width: 42, height: 42, borderRadius: 12, flexShrink: 0, display: "grid", placeItems: "center", color: "#fff",
                  background: `linear-gradient(135deg, ${alpha(color, 0.85)}, ${color})`, boxShadow: `0 8px 18px -8px ${alpha(color, 0.9)}`,
                }}><Sparkles size={19} /></span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ ...t.font.statLabel, color }}>AI read · what this thread was</div>
                  {topicDetailLoading ? (
                    <div style={{ marginTop: 8 }}><Shim h={18} w="86%" /><Shim h={18} w="58%" style={{ marginTop: 8 }} /></div>
                  ) : (
                    <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.35px", lineHeight: 1.35, color: t.c.text, marginTop: 4, maxWidth: "70ch" }}>{lead}</div>
                  )}
                  {!topicDetailLoading && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                      <Pill t={t} color={read.ai ? t.c.ai : color}>{read.ai ? "written from the work" : "computed from the hours"}</Pill>
                      <Pill t={t} color={color}>{fmtMins(topic.minutes)} · {items} piece{items === 1 ? "" : "s"}</Pill>
                      {read.points.length ? <Pill t={t} color={color}>{read.points.length} point{read.points.length === 1 ? "" : "s"}</Pill> : null}
                      {td && td.peakHour != null ? <Pill t={t} color={t.isDark ? "#F0B24A" : "#D97706"}>peak {fmtHour(td.peakHour)}</Pill> : null}
                    </div>
                  )}
                </div>
              </div>

              {!topicDetailLoading && rest ? (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ width: 3, height: 12, borderRadius: 2, background: color }} />
                    <span style={{ ...t.font.microLabel, color: t.c.text, fontWeight: 700 }}>The thread, in beats</span>
                  </div>
                  <BeatList t={t} text={rest} />
                </div>
              ) : null}

              {!topicDetailLoading && read.points.length ? (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ width: 3, height: 12, borderRadius: 2, background: color }} />
                    <span style={{ ...t.font.microLabel, color: t.c.text, fontWeight: 700 }}>{read.ai ? "What got done" : "The biggest pieces"}</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: read.points.length >= 4 ? "repeat(auto-fit, minmax(300px, 1fr))" : "1fr", gap: 8 }}>
                    {read.points.map((pt, k) => (
                      <div key={k} style={{
                        display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", borderRadius: 10,
                        background: alpha(color, t.isDark ? 0.1 : 0.06), boxShadow: `inset 0 0 0 1px ${alpha(color, 0.22)}`,
                      }}>
                        <span style={{ width: 22, height: 22, borderRadius: 7, flexShrink: 0, display: "grid", placeItems: "center", background: color, color: "#fff", fontSize: 10.5, fontWeight: 800, fontVariantNumeric: "tabular-nums", boxShadow: `0 3px 8px -3px ${alpha(color, 0.7)}` }}>{k + 1}</span>
                        <span style={{ fontSize: 13, lineHeight: 1.55, color: t.c.text }}>{pt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {!topicDetailLoading && (
                <div style={{ marginTop: 14, paddingTop: 11, borderTop: `1px solid ${alpha(color, 0.2)}`, display: "flex", alignItems: "center", gap: 8, fontSize: 11, lineHeight: 1.5, color: t.c.textSecondary, flexWrap: "wrap" }}>
                  <i aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 99, background: read.ai ? t.status.positive : color, flexShrink: 0 }} />
                  {read.ai
                    ? "Written from this thread's tracked activity — the times are exact and nothing is invented."
                    : "Composed from this thread's tracked figures — a written read from its content appears once indexed."}
                </div>
              )}
            </div>
          );
        })()}

        {/* evidence row: inside the thread · when it happened ────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginBottom: 14 }}>
          <div style={panel(t)}>
            <Overline t={t} accent={color} icon={<Layers size={12} />} label="Inside the thread"
              right={<Pill t={t} color={color}>{topic.items.length} piece{topic.items.length === 1 ? "" : "s"}</Pill>} />
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <TopicDonut t={t} items={topic.items} colors={itemColors} />
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {topic.items.slice(0, 5).map((it, k) => {
                  const share = topic.minutes ? Math.round((it.minutes / topic.minutes) * 100) : 0;
                  const c = itemColor(k);
                  const hasIcon = it.app && (toolGlyphIdFor(it.app) || brandColorFor(it.app, t));
                  return (
                    <button key={k} type="button" onClick={() => setItemIdx(k)} title={`${it.title} · ${fmtMins(it.minutes)} · open`}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "4px 6px", margin: "0 -6px", borderRadius: 8, border: 0, background: "transparent", cursor: "pointer", fontFamily: t.font.family, textAlign: "left" }}>
                      <span style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, display: "grid", placeItems: "center", background: c, color: "#fff", fontSize: 10, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{k + 1}</span>
                      {hasIcon ? <span style={{ display: "inline-flex", flexShrink: 0 }}><ToolIcon name={it.app} size={16} /></span> : null}
                      <span style={{ fontSize: 12.5, fontWeight: 500, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{it.title}</span>
                      <span style={{ width: 54, height: 5, borderRadius: 999, background: alpha(c, 0.15), overflow: "hidden", flexShrink: 0 }}>
                        <span style={{ display: "block", height: "100%", width: `${Math.max(4, share)}%`, background: c, borderRadius: 999 }} />
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 800, color: c, fontVariantNumeric: "tabular-nums", flexShrink: 0, minWidth: 34, textAlign: "right" }}>{share}%</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div style={panel(t)}>
            <Overline t={t} accent={color} icon={<Clock size={12} />} label="When it happened"
              right={td && td.peakHour != null ? <Pill t={t} color={t.isDark ? "#F0B24A" : "#D97706"}>peak {fmtHour(td.peakHour)}{td.span ? ` · ${fmtHour(td.span.from)}–${fmtHour(td.span.to)}` : ""}</Pill> : null} />
            {topicDetailLoading ? <SkBars t={t} /> : <HourBars t={t} hourly={td && td.hourly} color={color} />}
          </div>
        </div>

        {/* where it ran — apps in this thread (skeleton while fetching) */}
        {topicDetailLoading ? (
          <div style={panel(t, { marginBottom: 14 })}>
            <Overline t={t} icon={<AppWindow size={12} />} label="Where it ran" />
            <Shim h={9} r={999} />
            <div style={{ display: "flex", gap: 14, marginTop: 10 }}>
              {[84, 68, 56].map((w, k) => <Shim key={k} w={w} h={11} r={6} />)}
            </div>
          </div>
        ) : td && Array.isArray(td.apps) && td.apps.length ? (
          <div style={panel(t, { marginBottom: 14 })}>
            <Overline t={t} accent={color} icon={<AppWindow size={12} />} label="Where it ran"
              right={<Pill t={t} color={color}>{td.apps.filter((a) => a && a.minutes > 0).length} tool{td.apps.filter((a) => a && a.minutes > 0).length === 1 ? "" : "s"}</Pill>} />
            <AppsSplit t={t} apps={td.apps} color={color} colors={itemColors} />
          </div>
        ) : null}

        {/* the pieces → L3 */}
        <Overline t={t} accent={color} icon={<ListTree size={12} />} label="The pieces"
          right={<Pill t={t} color={color}>{topic.items.length} · click one for its full detail →</Pill>} />
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {topic.items.map((it, idx) => (
            <ItemRow key={idx} t={t} item={it} color={itemColor(idx)} index={idx} share={topic.minutes ? Math.round((it.minutes / topic.minutes) * 100) : 0} topItem={topItem} onOpen={() => setItemIdx(idx)} />
          ))}
        </div>
      </div>
    );
  }

  // ── LAYER 1 · the full-day picture: the brief + topic CARDS ────────────
  // A hero brief (AI headline + whole-day split) sits above a grid where each
  // TOPIC is its own card — accent chip, hours, a mini donut of its item split,
  // its top two items as shares, and the AI overview as a quote. Cards are the
  // doors to L2, so a click stays specific to the topic opened.
  const dayColors = topics.map((_, i) => topicColor(i));

  return (
    <div>
      {/* the brief — a branded hero that follows the theme (light in light mode,
          dark in dark mode): AI headline + whole-day split + rhythm */}
      <div style={{
        borderRadius: t.r.card, padding: "22px 24px", marginBottom: 18,
        background: `linear-gradient(180deg, ${alpha(t.c.primary, 0.07)}, ${t.c.surface} 58%)`,
        boxShadow: `0 0 0 1px ${t.c.border}, 0 1px 2px ${t.c.shadowColor}, 0 18px 40px -26px ${t.c.shadowColor}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 15 }}>
          <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 8, display: "grid", placeItems: "center", background: alpha(t.c.primary, 0.14), boxShadow: `inset 0 0 0 1px ${alpha(t.c.primary, 0.3)}`, color: t.c.primary, flexShrink: 0 }}>
            <Sparkles size={13} />
          </span>
          <span style={{ ...t.font.statLabel, color: t.c.primary }}>What the day was</span>
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 650, color: t.c.text, padding: "5px 11px", borderRadius: 999, background: alpha(t.c.primary, 0.1), border: `1px solid ${alpha(t.c.primary, 0.3)}`, fontVariantNumeric: "tabular-nums" }}>
            <i aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 99, background: t.c.primary }} />
            {topics.length} thread{topics.length === 1 ? "" : "s"} · {fmtMins(total)} active
          </span>
        </div>

        {data.headline ? (
          <div style={{ margin: "0 0 18px" }}>
            <BeatList t={t} text={data.headline} />
          </div>
        ) : null}

        <span style={{ display: "flex", height: 11, borderRadius: 999, overflow: "hidden", background: t.c.surfaceMuted, boxShadow: `inset 0 0 0 1px ${t.c.border}` }}>
          {topics.map((tp, i) => {
            const frac = total > 0 ? (tp.minutes / total) * 100 : 0;
            return <span key={tp.id} title={`${tp.title} · ${fmtMins(tp.minutes)}`} style={{ width: `${frac}%`, background: dayColors[i], borderRight: i < topics.length - 1 ? `2px solid ${t.c.surface}` : "none", opacity: hotThread == null || hotThread === i ? 1 : 0.25, transition: "opacity .15s ease" }} />;
          })}
        </span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", marginTop: 13 }}>
          {topics.map((tp, i) => {
            const pct = total ? Math.round((tp.minutes / total) * 100) : 0;
            return (
              <button key={tp.id} type="button" onClick={() => { setTopicId(tp.id); setItemIdx(null); }}
                onMouseEnter={() => setHotThread(i)} onMouseLeave={() => setHotThread(null)}
                title={`${tp.title} · ${fmtMins(tp.minutes)} · open`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  color: t.c.text, padding: "5px 11px 5px 8px", borderRadius: 999, fontFamily: t.font.family,
                  background: alpha(dayColors[i], 0.12), border: `1px solid ${alpha(dayColors[i], 0.4)}`,
                  opacity: hotThread == null || hotThread === i ? 1 : 0.4, transition: "opacity .15s ease, transform .15s ease",
                  transform: hotThread === i ? "translateY(-1px)" : "none",
                }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: dayColors[i], flexShrink: 0 }} />
                <span style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tp.title}</span>
                <b style={{ color: dayColors[i], fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{pct}%</b>
                <span style={{ color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>· {fmtMins(tp.minutes)}</span>
              </button>
            );
          })}
        </div>

        {data.rhythm && data.rhythm.blocks && data.rhythm.blocks.length ? (
          <div style={{ marginTop: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 11 }}>
              <span style={{ width: 3, height: 12, borderRadius: 2, background: t.c.primary }} />
              <span style={{ ...t.font.microLabel, color: t.c.text }}>Day rhythm</span>
              <span style={{ marginLeft: "auto" }}><Pill t={t} color={t.c.primary}>hover a block to see its thread</Pill></span>
            </div>
            <Rhythm t={t} rhythm={data.rhythm} dayColors={dayColors} topics={topics} />
          </div>
        ) : null}
      </div>

      {/* the threads — doors into layer 2. Graphical callers (palette set)
          get hairline rows that cross-light the share bar above; classic
          keeps its card grid untouched. */}
      <Overline t={t} accent={t.c.primary} icon={<ListTree size={12} />} label="What the day broke into" strong
        right={<Pill t={t} color={t.c.primary}>{topics.length} thread{topics.length === 1 ? "" : "s"} · click one to open it →</Pill>} />
      {Array.isArray(palette) && palette.length ? (
        <div style={panel(t, { padding: "6px 6px" })} onMouseLeave={() => setHotThread(null)}>
          {topics.map((tp, i) => (
            <TopicRow
              key={tp.id} t={t} topic={tp} color={dayColors[i]} index={i} total={total}
              onHot={setHotThread}
              onOpen={() => { setTopicId(tp.id); setItemIdx(null); }}
            />
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {topics.map((tp, i) => (
            <TopicCard
              key={tp.id} t={t} topic={tp} color={dayColors[i]} index={i} total={total}
              onOpen={() => { setTopicId(tp.id); setItemIdx(null); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── design system ─────────────────────────────────────────────────────── */

/** The one card surface — a consistent elevation scale across every layer. */
function panel(t, extra) {
  return {
    padding: "16px 17px", borderRadius: t.r.card, background: t.c.surface,
    boxShadow: `0 0 0 1px ${t.c.border}, 0 1px 2px ${t.c.shadowColor}, 0 14px 34px -24px ${t.c.shadowColor}`,
    ...extra,
  };
}

/** Section header: an accent tick + optional icon + label + right slot. Used
 *  everywhere so the layers share one visual grammar. */
function Overline({ t, accent, icon, label, right, strong }) {
  const c = accent || t.c.primary;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 13 }}>
      <span style={{ width: 3, height: 14, borderRadius: 2, background: c, flexShrink: 0 }} />
      {icon ? (
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: 6, background: alpha(c, 0.14), boxShadow: `inset 0 0 0 1px ${alpha(c, 0.3)}`, color: c, flexShrink: 0 }}>{icon}</span>
      ) : null}
      <span style={{ ...t.font.microLabel, color: strong ? t.c.text : t.c.textSecondary, fontWeight: 700 }}>{label}</span>
      {right != null ? <div style={{ marginLeft: "auto" }}>{right}</div> : null}
    </div>
  );
}

/** A big tabular figure with an optional caption — the hours anchor. */
function Metric({ t, value, caption }) {
  return (
    <div style={{ textAlign: "right", flexShrink: 0 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: t.c.text, letterSpacing: "-0.6px", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{value}</div>
      {caption ? <div style={{ ...t.font.microLabel, color: t.c.textTertiary, marginTop: 5 }}>{caption}</div> : null}
    </div>
  );
}

/** The accent number chip that identifies a thread across L1/L2. */
function NumTile({ color, n, size = 38 }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: Math.round(size * 0.34), flexShrink: 0,
      display: "grid", placeItems: "center", background: alpha(color, 0.15), color,
      fontSize: Math.round(size * 0.42), fontWeight: 800, fontVariantNumeric: "tabular-nums",
      boxShadow: `inset 0 0 0 1px ${alpha(color, 0.3)}`,
    }}>{n}</span>
  );
}

/** A small rounded cue pill (e.g. "written read" / "computed read"). */
function Pill({ t, color, children }) {
  const c = color || t.c.primary;
  return (
    <span style={{
      ...t.font.microLabel,
      color: c, background: alpha(c, 0.12), boxShadow: `inset 0 0 0 1px ${alpha(c, 0.3)}`,
      borderRadius: 999, padding: "3px 9px", whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function Badge({ t, color, children }) {
  return (
    <span style={{
      ...t.font.microLabel,
      color, background: alpha(color, 0.13), padding: "3px 7px", borderRadius: 6,
      flexShrink: 0, whiteSpace: "nowrap", boxShadow: `inset 0 0 0 1px ${alpha(color, 0.2)}`,
    }}>{children}</span>
  );
}

function Stat({ t, k, v, bar, c }) {
  return (
    <div style={{ padding: "11px 13px", borderRadius: t.r.card, background: t.c.surfaceMuted, boxShadow: `inset 0 0 0 1px ${t.c.border}` }}>
      <div style={{ ...t.font.microLabel, color: t.c.textTertiary }}>{k}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: t.c.text, marginTop: 4, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.3px" }}>{v}</div>
      {bar != null ? (
        <span style={{ display: "block", height: 4, background: t.c.surface, borderRadius: 999, overflow: "hidden", marginTop: 9 }}>
          <span style={{ display: "block", height: "100%", borderRadius: 999, width: `${Math.max(3, bar)}%`, background: c || t.c.primary }} />
        </span>
      ) : null}
    </div>
  );
}

/** One clickable item row (a door into L3), with a hover accent. */
function ItemRow({ t, item, color, topItem, onOpen, index = 0, share: shareOfThread = 0 }) {
  const [hover, setHover] = React.useState(false);
  const share = topItem ? Math.max(5, (item.minutes / topItem) * 100) : 5;
  /* The badge already names the app — repeating it inside the raw window
     title (" - Google Chrome") is display noise. Segments equal to the app
     are dropped from the SHOWN title only; the full title stays on hover. */
  const shownTitle = React.useMemo(() => {
    const raw = String(item.title || "");
    if (!item.app) return raw;
    const appLc = String(item.app).trim().toLowerCase();
    const parts = raw.split(" - ").filter((seg) => seg.trim().toLowerCase() !== appLc);
    return parts.join(" - ") || raw;
  }, [item.title, item.app]);
  return (
    <button
      type="button" onClick={onOpen}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)} onBlur={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
        padding: "12px 14px", borderRadius: t.r.card, cursor: "pointer", fontFamily: t.font.family,
        background: hover ? alpha(color, 0.05) : t.c.surface,
        border: `1px solid ${hover ? alpha(color, 0.5) : t.c.border}`,
        boxShadow: hover ? `0 10px 22px -18px ${alpha(color, 0.6)}` : "none",
        transition: "border-color .15s, background .15s, box-shadow .15s",
      }}
    >
      <NumTile color={color} n={index + 1} size={32} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
          {item.app ? ((toolGlyphIdFor(item.app) || brandColorFor(item.app, t)) ? <span title={item.app} style={{ display: "inline-flex", verticalAlign: "middle", flexShrink: 0 }}><ToolIcon name={item.app} size={20} /></span> : <Badge t={t} color={color}>{item.app}</Badge>) : null}
          <span style={{ fontSize: 13.5, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 650, minWidth: 0 }} title={item.title}>{shownTitle}</span>
          <span style={{ fontSize: 10.5, fontWeight: 800, color, background: alpha(color, 0.12), boxShadow: `inset 0 0 0 1px ${alpha(color, 0.3)}`, borderRadius: 999, padding: "2px 7px", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{shareOfThread}% of the thread</span>
        </div>
        <span style={{ display: "block", height: 6, background: alpha(color, 0.12), borderRadius: 999, overflow: "hidden" }}>
          <span style={{ display: "block", height: "100%", borderRadius: 999, width: `${share}%`, background: color }} />
        </span>
      </div>
      <span style={{ display: "inline-flex", alignItems: "center", fontSize: 12.5, fontWeight: 750, color, padding: "5px 10px", borderRadius: 999, background: alpha(color, 0.12), boxShadow: `inset 0 0 0 1px ${alpha(color, 0.3)}`, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmtMins(item.minutes)}</span>
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 999, background: hover ? color : alpha(color, 0.12), color: hover ? "#fff" : color, flexShrink: 0, transition: "background .15s, color .15s" }}>
        <ChevronRight size={14} style={{ transform: hover ? "translateX(1px)" : "none", transition: "transform .15s" }} />
      </span>
    </button>
  );
}

function Crumbs({ t, trail, onBack }) {
  /* Same language as the page breadcrumb (2026-08-30): filled ink circle to
     step back, links as ink pills, the current level a bold filled pill,
     chevrons in ink. */
  const p = t.c.primary;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
      <button type="button" onClick={onBack} aria-label="Back" title="Back" style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 999,
        color: "#fff", border: "none", background: p, cursor: "pointer", fontFamily: t.font.family, padding: 0, flexShrink: 0,
        boxShadow: `0 4px 12px ${alpha(p, 0.35)}`,
      }}><ArrowLeft size={14} /></button>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 }}>
        {trail.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight size={14} style={{ color: p, opacity: 0.7, flexShrink: 0 }} />}
            {c.on ? (
              <button type="button" onClick={c.on} title={c.label} style={{
                display: "inline-flex", alignItems: "center", maxWidth: 260, fontSize: 12.5, fontWeight: 600, lineHeight: 1,
                padding: "7px 11px", borderRadius: 999, color: p, background: alpha(p, 0.08), border: `1px solid ${alpha(p, 0.2)}`,
                cursor: "pointer", fontFamily: t.font.family, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>{c.label}</button>
            ) : (
              <span aria-current="page" title={c.label} style={{
                display: "inline-flex", alignItems: "center", maxWidth: 360, fontSize: 12.5, fontWeight: 700, lineHeight: 1,
                padding: "7px 12px", borderRadius: 999, color: t.c.text, background: t.c.surface, border: `1px solid ${t.c.border}`,
                boxShadow: `0 1px 2px ${t.c.shadowColor}`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>{c.label}</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function Note({ t, children }) {
  return (
    <div style={panel(t, { fontSize: 13, lineHeight: 1.6, color: t.c.textSecondary })}>
      {children}
    </div>
  );
}

/* Apply an opacity to a theme colour (hex/rgb/hsl) so a card can stay
   mono-accented — the donut segments are tints of the one topic colour. */
function alpha(color, a) {
  const c = String(color || "");
  const v = Math.max(0, Math.min(1, a));
  const hx = () => Math.round(v * 255).toString(16).padStart(2, "0");
  if (/^#[0-9a-f]{6}$/i.test(c)) return c + hx();
  if (/^#[0-9a-f]{3}$/i.test(c)) return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}${hx()}`;
  const rgb = c.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) return `rgba(${rgb[1].split(",").slice(0, 3).map((x) => x.trim()).join(", ")}, ${v})`;
  const hsl = c.match(/^hsla?\(([^)]+)\)$/i);
  if (hsl) return `hsla(${hsl[1].split(",").slice(0, 3).map((x) => x.trim()).join(", ")}, ${v})`;
  return c;
}

/* A compact donut of the item split WITHIN one topic — tints of the topic's
   colour so the card reads as one accent. Falls back to a soft ring when the
   topic has no per-item breakdown. */
function TopicDonut({ t, items, color, colors }) {
  const S = 84, R = 38, rin = 25, cx = S / 2, cy = S / 2;
  const list = (items || []).slice(0, 5);
  const sum = list.reduce((a, x) => a + (x.minutes || 0), 0) || 1;
  const cFor = (i) => (Array.isArray(colors) && colors.length ? colors[i % colors.length] : alpha(color || t.c.primary, 1 - i * 0.16));
  if (!list.length) {
    return <span style={{ width: S, height: S, borderRadius: "50%", flexShrink: 0, background: alpha(color || t.c.primary, 0.12), boxShadow: `inset 0 0 0 9px ${alpha(color || t.c.primary, 0.06)}` }} />;
  }
  let ang = -Math.PI / 2;
  const arcs = list.map((it, i) => {
    const frac = (it.minutes || 0) / sum;
    const a0 = ang, a1 = ang + frac * 2 * Math.PI; ang = a1;
    const big = a1 - a0 > Math.PI ? 1 : 0;
    const pt = (a, r) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    const [x0, y0] = pt(a0, R), [x1, y1] = pt(a1, R), [x2, y2] = pt(a1, rin), [x3, y3] = pt(a0, rin);
    return { d: `M${x0} ${y0} A${R} ${R} 0 ${big} 1 ${x1} ${y1} L${x2} ${y2} A${rin} ${rin} 0 ${big} 0 ${x3} ${y3} Z`, c: cFor(i), k: i, title: `${it.title} · ${Math.round(frac * 100)}%` };
  });
  return (
    <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`} style={{ flexShrink: 0 }} role="img" aria-label="Item split within topic">
      {arcs.map((a) => <path key={a.k} d={a.d} fill={a.c} stroke={t.c.surface} strokeWidth="2"><title>{a.title}</title></path>)}
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 15, fontWeight: 800, fill: t.c.text, fontFamily: t.font.family }}>{list.length}</text>
    </svg>
  );
}

/* Renders the headline with one key phrase emphasised in the accent colour.
   Uses the surrounding font — only the colour/weight of the key changes. */
function Headline({ text, keyPhrase, color }) {
  const s = String(text || "");
  if (!keyPhrase || !s.includes(keyPhrase)) return <>{s}</>;
  const i = s.indexOf(keyPhrase);
  return <>{s.slice(0, i)}<span style={{ color, fontWeight: 600 }}>{keyPhrase}</span>{s.slice(i + keyPhrase.length)}</>;
}

/* minutes-of-day → "9 AM" / "6:30 PM", for the rhythm axis. */
function fmtClock(min) {
  const total = ((Math.round(min) % 1440) + 1440) % 1440;
  let h = Math.floor(total / 60); const m = total % 60;
  const ap = h < 12 ? "AM" : "PM";
  h = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h}:${String(m).padStart(2, "0")} ${ap}` : `${h} ${ap}`;
}

/* DAY RHYTHM — the real activity ordered across the day as coloured blocks
   (one colour per thread), idle time left as the hatched theme base. Fully
   theme-token driven so it matches light and dark. */
function Rhythm({ t, rhythm, dayColors, topics }) {
  const startMin = rhythm.startMin, endMin = rhythm.endMin;
  const blocks = rhythm.blocks || [];
  const span = Math.max(1, endMin - startMin);
  const pos = (m) => `${((m - startMin) / span) * 100}%`;
  const wid = (a, b) => `${(Math.max(0, b - a) / span) * 100}%`;
  const labels = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ f, txt: fmtClock(startMin + f * span) }));
  return (
    <div>
      <div style={{
        position: "relative", height: 34, borderRadius: 9, overflow: "hidden",
        background: `repeating-linear-gradient(-45deg, ${t.c.surfaceMuted} 0 6px, ${t.c.surface} 6px 12px)`,
        boxShadow: `inset 0 0 0 1px ${t.c.border}`,
      }}>
        {blocks.map((b, i) => {
          const c = b.topic >= 0 && dayColors[b.topic] ? dayColors[b.topic] : t.c.borderStrong;
          const label = b.topic >= 0 && topics[b.topic] ? topics[b.topic].title : "Other activity";
          return (
            <span key={i} title={`${label} · ${fmtClock(b.s)}–${fmtClock(b.e)}`}
              style={{ position: "absolute", top: 3, bottom: 3, left: pos(b.s), width: wid(b.s, b.e), background: c, borderRadius: 4, opacity: b.topic >= 0 ? 1 : 0.5 }} />
          );
        })}
      </div>
      <div style={{ position: "relative", height: 13, marginTop: 6 }}>
        {labels.map((l, i) => (
          <span key={i} style={{
            position: "absolute", left: `${l.f * 100}%`, fontSize: 9.5, color: t.c.textTertiary, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
            transform: i === 0 ? "none" : i === labels.length - 1 ? "translateX(-100%)" : "translateX(-50%)",
          }}>{l.txt}</span>
        ))}
      </div>
    </div>
  );
}

/* 0-23 → "9a" / "2p" / "12p", for the when-it-happened axis. */
function fmtHour(h) {
  const n = ((Number(h) % 24) + 24) % 24;
  const hr = n % 12 === 0 ? 12 : n % 12;
  return `${hr} ${n < 12 ? "AM" : "PM"}`;
}

/* The "when it happened" chart — per-hour active minutes for one topic, cropped
   to the hours it actually touched so the shape is legible. */
function HourBars({ t, hourly, color }) {
  const list = (hourly || []).filter((x) => x && Number.isFinite(x.h));
  const active = list.filter((x) => x.minutes > 0);
  if (!active.length) {
    return <div style={{ fontSize: 11.5, color: t.c.textTertiary, lineHeight: 1.5 }}>No time-of-day detail available for this thread yet.</div>;
  }
  const from = Math.min(...active.map((x) => x.h));
  const to = Math.max(...active.map((x) => x.h));
  const span = list.filter((x) => x.h >= from && x.h <= to);
  const max = Math.max(...span.map((x) => x.minutes), 1);
  const peak = span.reduce((a, b) => (b.minutes > a.minutes ? b : a), span[0]);
  const H = 92, floor = 16;
  const gold = t.isDark ? "#F0B24A" : "#D97706";
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: H }}>
        {span.map((x) => {
          const on = x.minutes > 0;
          const hpx = on ? Math.max(4, Math.round((x.minutes / max) * (H - floor))) : 3;
          const isPeak = on && x.h === peak.h;
          return (
            <div key={x.h} title={`${fmtHour(x.h)} · ${fmtMins(x.minutes)}`}
              style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", height: "100%" }}>
              <span style={{ width: "100%", maxWidth: 24, height: hpx, borderRadius: 4, background: on ? (isPeak ? gold : `linear-gradient(180deg, ${color}, ${alpha(color, 0.7)})`) : alpha(color, 0.18), boxShadow: isPeak ? `0 6px 14px -6px ${alpha(gold, 0.9)}` : "none", transition: "height .3s cubic-bezier(.16,1,.3,1)" }} />
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 3, marginTop: 6 }}>
        {span.map((x, i) => (
          <span key={x.h} style={{ flex: 1, textAlign: "center", fontSize: 9.5, fontWeight: x.h === peak.h ? 800 : 600, color: x.h === peak.h ? gold : t.c.textSecondary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
            {(span.length <= 8 || i % 2 === 0) ? fmtHour(x.h) : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

/* The "where it ran" split — a slim segmented bar of the apps this topic ran
   in, tints of the one topic colour, with a legend. */
function AppsSplit({ t, apps, color, colors }) {
  const list = (apps || []).filter((a) => a && a.minutes > 0).slice(0, 6);
  if (!list.length) return null;
  const total = list.reduce((a, x) => a + x.minutes, 0) || 1;
  /* Each tool wears its brand colour where one is known, else a categorical
     ink — never six tints of one blue. */
  const cFor = (a, i) => brandColorFor(a.app, t) || (Array.isArray(colors) && colors.length ? colors[i % colors.length] : alpha(color, 1 - i * 0.14));
  const iconFor = (a) => (a.app && (toolGlyphIdFor(a.app) || brandColorFor(a.app, t)) ? <ToolIcon name={a.app} size={16} /> : null);
  /* A split bar with one segment is a chart that says nothing — a single
     app becomes a plain sentence. */
  if (list.length === 1) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, color: t.c.textSecondary }}>
        {iconFor(list[0]) || <span style={{ width: 9, height: 9, borderRadius: 3, background: cFor(list[0], 0), flexShrink: 0 }} />}
        All of it ran in <b style={{ color: t.c.text, fontWeight: 700 }}>{list[0].app}</b>
        <span style={{ color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>· {fmtMins(list[0].minutes)}</span>
      </div>
    );
  }
  return (
    <div>
      <span style={{ display: "flex", height: 12, borderRadius: 999, overflow: "hidden", background: t.c.surfaceMuted, boxShadow: `inset 0 0 0 1px ${t.c.border}` }}>
        {list.map((a, i) => (
          <span key={i} title={`${a.app} · ${fmtMins(a.minutes)} · ${Math.round((a.minutes / total) * 100)}%`} style={{ width: `${(a.minutes / total) * 100}%`, minWidth: 3, background: cFor(a, i), borderRight: i < list.length - 1 ? `2px solid ${t.c.surface}` : "none" }} />
        ))}
      </span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "7px 16px", marginTop: 10 }}>
        {list.map((a, i) => (
          <span key={i} title={`${a.app} · ${fmtMins(a.minutes)}`} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: t.c.text, padding: "5px 10px 5px 7px", borderRadius: 999, background: alpha(cFor(a, i), 0.12), border: `1px solid ${alpha(cFor(a, i), 0.4)}` }}>
            {iconFor(a) || <span style={{ width: 9, height: 9, borderRadius: 3, background: cFor(a, i), flexShrink: 0 }} />}
            <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.app}</span>
            <b style={{ color: cFor(a, i), fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{fmtMins(a.minutes)}</b>
            <span style={{ color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>· {Math.round((a.minutes / total) * 100)}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* One topic, as a card (the L1 grid tile): accent chip + hours, a donut of its
   item split, its top two items as shares, and the AI overview as a quote. The
   whole card is the button into layer 2 for THIS topic. */
/** Rows-mode thread door: one hairline row per thread — dot, title, the AI
 *  overview, its top item, real minutes and a chevron. Hover slides the row
 *  and lights this thread's slice in the share bar above (onHot). */
function TopicRow({ t, topic, color, index, total, onOpen, onHot }) {
  const [hover, setHover] = React.useState(false);
  const items = topic.items || [];
  const pctOfDay = total ? Math.round((topic.minutes / total) * 100) : 0;
  const top = items[0];
  const topShare = top && topic.minutes ? Math.round((top.minutes / topic.minutes) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      onMouseEnter={() => { setHover(true); onHot?.(index); }}
      onMouseLeave={() => setHover(false)}
      onFocus={() => { setHover(true); onHot?.(index); }}
      onBlur={() => { setHover(false); onHot?.(null); }}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12, width: "100%", textAlign: "left",
        padding: "13px 14px", border: 0,
        borderTop: index ? `1px solid ${t.c.border}` : "none",
        background: hover ? t.c.surfaceMuted : "transparent",
        borderRadius: 12,
        transform: hover ? "translateX(4px)" : "none",
        transition: "background .15s ease, transform .15s ease",
        cursor: "pointer", fontFamily: t.font.family, color: t.c.text,
      }}
    >
      <NumTile color={color} n={index + 1} size={34} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "-0.2px", minWidth: 0 }} title={topic.title}>{topic.title}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color, background: alpha(color, 0.12), boxShadow: `inset 0 0 0 1px ${alpha(color, 0.3)}`, borderRadius: 999, padding: "2px 8px", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
            {pctOfDay}% of the day
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, color: t.c.textSecondary, background: t.c.surfaceMuted, boxShadow: `inset 0 0 0 1px ${t.c.border}`, borderRadius: 999, padding: "2px 8px", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
            {items.length} item{items.length === 1 ? "" : "s"}
          </span>
        </span>
        {topic.overview ? (
          <span style={{
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
            fontSize: 12.5, lineHeight: 1.55, color: t.c.textSecondary, marginTop: 4, maxWidth: "82ch",
          }}>{topic.overview}</span>
        ) : null}
        {top ? (
          <span style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 7, minWidth: 0 }}>
            {top.app && (toolGlyphIdFor(top.app) || brandColorFor(top.app, t)) ? (
              <span title={top.app} style={{ display: "inline-flex", flexShrink: 0 }}><ToolIcon name={top.app} size={16} /></span>
            ) : (
              <span style={{ ...t.font.microLabel, color, flexShrink: 0 }}>Top</span>
            )}
            <span style={{ fontSize: 12, color: t.c.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }} title={top.title}>{top.title}</span>
            <span style={{ flexShrink: 0, width: 64, height: 4, borderRadius: 999, background: t.c.surfaceMuted, overflow: "hidden" }}>
              <span style={{ display: "block", height: "100%", width: `${Math.max(6, topShare)}%`, background: color, borderRadius: 999 }} />
            </span>
            <b style={{ fontSize: 11, color, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{topShare}%</b>
          </span>
        ) : null}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", fontSize: 13, fontWeight: 750, color, fontVariantNumeric: "tabular-nums", flexShrink: 0, padding: "5px 10px", borderRadius: 999, background: alpha(color, 0.12), boxShadow: `inset 0 0 0 1px ${alpha(color, 0.3)}` }}>
        {fmtMins(topic.minutes)}
      </span>
      <ArrowRight size={15} style={{ color, flexShrink: 0, marginTop: 3, transform: hover ? "translateX(3px)" : "none", transition: "transform .18s" }} />
    </button>
  );
}

function TopicCard({ t, topic, color, index, total, onOpen }) {
  const [hover, setHover] = React.useState(false);
  const items = topic.items || [];
  const topItems = items.slice(0, 2);
  const moreCount = Math.max(0, items.length - topItems.length);
  const pctOfDay = total ? Math.round((topic.minutes / total) * 100) : 0;
  return (
    <button
      type="button" onClick={onOpen}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)} onBlur={() => setHover(false)}
      style={{
        display: "flex", flexDirection: "column", gap: 0, width: "100%", height: "100%", textAlign: "left",
        padding: 0, borderRadius: t.r.card, cursor: "pointer", fontFamily: t.font.family,
        background: t.c.surface,
        border: `1px solid ${hover ? alpha(color, 0.55) : t.c.border}`,
        boxShadow: hover
          ? `0 0 0 1px ${alpha(color, 0.3)}, 0 20px 42px -22px ${alpha(color, 0.6)}`
          : `0 0 0 1px ${t.c.border}, 0 1px 2px ${t.c.shadowColor}, 0 12px 30px -24px ${t.c.shadowColor}`,
        transform: hover ? "translateY(-3px)" : "none",
        transition: "border-color .18s, box-shadow .18s, transform .18s",
        position: "relative", overflow: "hidden",
      }}
    >
      {/* accent top rail — one colour per thread */}
      <span aria-hidden="true" style={{ height: 4, background: color }} />

      <div style={{ padding: "15px 17px 16px", display: "flex", flexDirection: "column", gap: 13, flex: 1 }}>
        {/* header: chip + title */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <NumTile color={color} n={index + 1} size={26} />
          <span style={{ fontSize: 15, fontWeight: 700, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.2px" }} title={topic.title}>{topic.title}</span>
        </div>

        {/* the hours, big + coloured, with the item-split ring */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 29, fontWeight: 700, color, letterSpacing: "-1.2px", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{fmtMins(topic.minutes)}</div>
            <div style={{ fontSize: 11.5, color: t.c.textTertiary, marginTop: 7, fontWeight: 500 }}>{items.length} item{items.length === 1 ? "" : "s"} · {pctOfDay}% of the day</div>
          </div>
          <TopicDonut t={t} items={items} color={color} />
        </div>

        {/* top-two items as shares */}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {topItems.length ? topItems.map((it, k) => {
            const share = topic.minutes ? Math.round((it.minutes / topic.minutes) * 100) : 0;
            return (
              <div key={k} style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 12, color: t.c.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }} title={it.title}>{it.title}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: t.c.text, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{share}%</span>
                </div>
                <span style={{ display: "block", height: 4, background: t.c.surfaceMuted, borderRadius: 999, overflow: "hidden" }}>
                  <span style={{ display: "block", height: "100%", width: `${Math.max(5, share)}%`, borderRadius: 999, background: alpha(color, k === 0 ? 1 : 0.55) }} />
                </span>
              </div>
            );
          }) : (
            <span style={{ fontSize: 11.5, color: t.c.textTertiary }}>No detailed items in this thread.</span>
          )}
          {moreCount > 0 ? <div style={{ fontSize: 11, color: t.c.textTertiary, fontWeight: 500, marginTop: 1 }}>+ {moreCount} more item{moreCount === 1 ? "" : "s"}</div> : null}
        </div>

        {/* the AI overview — our font, not italic — with a divider above */}
        {topic.overview ? (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", borderTop: `1px solid ${t.c.border}`, paddingTop: 13 }}>
            <Sparkles size={13} style={{ color, flexShrink: 0, marginTop: 2 }} />
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: t.c.textSecondary }}>{topic.overview}</p>
          </div>
        ) : null}

        {/* footer cue — pinned to the bottom so every card's row aligns */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, letterSpacing: 0.2, color, marginTop: "auto", paddingTop: 2 }}>
          Open thread <ArrowRight size={14} style={{ transform: hover ? "translateX(3px)" : "none", transition: "transform .18s" }} />
        </div>
      </div>
    </button>
  );
}

/* Skeleton of a TopicCard — same shape, so the L1 grid doesn't reflow when the
   real cards arrive. */
function SkTopicCard({ t }) {
  return (
    <div style={{ borderRadius: t.r.card, background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}`, overflow: "hidden" }} aria-hidden>
      <span style={{ display: "block", height: 4, background: t.c.borderStrong }} />
      <div style={{ padding: "15px 17px 16px", display: "flex", flexDirection: "column", gap: 13 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Shim w={26} h={26} r={9} />
          <Shim w="60%" h={13} />
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Shim w={96} h={26} r={7} />
            <Shim w="70%" h={10} style={{ marginTop: 9 }} />
          </div>
          <Shim w={64} h={64} r={999} />
        </div>
        <div>
          <Shim h={11} /><Shim h={4} r={999} style={{ marginTop: 6 }} />
          <Shim h={11} style={{ marginTop: 11 }} /><Shim h={4} r={999} style={{ marginTop: 6 }} />
        </div>
        <div style={{ borderTop: `1px solid ${t.c.border}`, paddingTop: 13 }}>
          <Shim h={12} w="94%" /><Shim h={12} w="80%" style={{ marginTop: 6 }} />
        </div>
      </div>
    </div>
  );
}

/* Skeleton for the "when it happened" bar chart — a row of shimmer bars, so the
   loading state reads as a chart, not a plain block. */
function SkBars({ t }) {
  const heights = [42, 66, 82, 58, 72, 48, 63, 52];
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 92 }} aria-hidden>
      {heights.map((h, k) => (
        <div key={k} style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "flex-end", height: "100%" }}>
          <Shim w={22} h={`${h}%`} r={4} />
        </div>
      ))}
    </div>
  );
}

export default DayPicture;
